// src/services/notifyApi.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense Notify — API client (backend /api/v1/notify)
// Every call takes an optional AbortSignal; errors carry .status, .code and the server message.
// ─────────────────────────────────────────────────────────────────────────────
import { config } from '../config';
import { getAuthToken } from '../utils/auth';

// config.apiUrl is …/api/v1 in FlowSense; tolerate …/api or a bare host
export const NOTIFY_BASE = (() => {
    const raw = String(config.apiUrl || '').replace(/\/+$/, '');
    if (/\/v1$/.test(raw)) return `${raw}/notify`;
    if (/\/api$/.test(raw)) return `${raw}/v1/notify`;
    return `${raw}/api/v1/notify`;
})();

const qs = (params = {}) => {
    const p = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '' && v !== 'all');
    return p.length ? `?${new URLSearchParams(p).toString()}` : '';
};

async function request(path, { method = 'GET', body, signal, auth = true } = {}) {
    const token = auth ? getAuthToken() : null;
    let res;
    try {
        res = await fetch(`${NOTIFY_BASE}${path}`, {
            method,
            signal,
            headers: {
                Accept: 'application/json',
                ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: body !== undefined ? JSON.stringify(body) : undefined,
        });
    } catch (e) {
        if (e?.name === 'AbortError') throw e;
        const err = new Error('Cannot reach the FlowSense server. Check your connection.');
        err.status = 0;
        throw err;
    }
    let json = null;
    try { json = await res.json(); } catch (_) { json = null; }
    if (!res.ok || json?.success === false) {
        const err = new Error(json?.message || (res.status === 403 ? 'You do not have permission for this.' : `Request failed (${res.status})`));
        err.status = res.status;
        err.code = json?.code;
        throw err;
    }
    return json;
}

const data = (p) => p.then(j => j?.data);

// ── catalog / stream ─────────────────────────────────────────────────────────
export const getCatalog = (signal) => data(request('/catalog', { signal }));
export const getStreamTicket = (signal) => data(request('/stream/ticket', { method: 'POST', body: {}, signal }));
export const streamUrl = (ticket, lastEventId) => `${NOTIFY_BASE}/stream${qs({ ticket, lastEventId })}`;

// ── inbox ────────────────────────────────────────────────────────────────────
/** @returns {Promise<{items:Array, nextCursor:string|null}>} */
export const getInbox = async (params = {}, signal) => {
    const j = await request(`/inbox${qs(params)}`, { signal });
    return { items: j.data || [], nextCursor: j.nextCursor || null };
};
export const getSummary = (signal) => data(request('/inbox/summary', { signal }));
export const getDigest = (signal) => data(request('/inbox/digest', { signal }));
export const getNotification = (id, signal) => data(request(`/inbox/${encodeURIComponent(id)}`, { signal }));
export const markRead = (ids) => data(request('/inbox/read', { method: 'POST', body: { ids: [].concat(ids) } }));
export const markUnread = (ids) => data(request('/inbox/unread', { method: 'POST', body: { ids: [].concat(ids) } }));
export const readAll = (category) => data(request('/inbox/read-all', { method: 'POST', body: category ? { category } : {} }));
export const acknowledge = (id) => data(request(`/inbox/${encodeURIComponent(id)}/ack`, { method: 'POST', body: {} }));
export const archive = (ids) => data(request('/inbox/archive', { method: 'POST', body: { ids: [].concat(ids) } }));
export const unarchive = (ids) => data(request('/inbox/unarchive', { method: 'POST', body: { ids: [].concat(ids) } }));
export const snooze = (id, minutes) => data(request(`/inbox/${encodeURIComponent(id)}/snooze`, { method: 'POST', body: { minutes } }));
export const removeNotifications = (ids) => data(request('/inbox/delete', { method: 'POST', body: { ids: [].concat(ids) } }));

// ── preferences / devices ────────────────────────────────────────────────────
export const getPreferences = (signal) => data(request('/preferences', { signal }));
export const savePreferences = (body) => data(request('/preferences', { method: 'PUT', body }));
export const getDevices = (signal) => data(request('/devices', { signal }));
export const registerDevice = (body) => data(request('/devices', { method: 'POST', body }));
export const removeDevice = (id) => data(request(`/devices/${encodeURIComponent(id)}`, { method: 'DELETE' }));
export const unregisterEndpoint = (endpoint) => data(request('/devices/unregister', { method: 'POST', body: { endpoint } }));
export const getWebPushKey = (signal) => data(request('/webpush/public-key', { signal, auth: false }));
export const sendTest = (priority) => data(request('/test', { method: 'POST', body: priority ? { priority } : {} }));

// ── console ──────────────────────────────────────────────────────────────────
export const getAudienceOptions = (search, signal) => data(request(`/audience/options${qs({ search })}`, { signal }));
export const previewNotification = (body, signal) => data(request('/preview', { method: 'POST', body, signal }));
export const sendNotification = (body) => request('/send', { method: 'POST', body });
/** @returns {Promise<{rows:Array, total:number}>} */
export const listDispatches = async (params = {}, signal) => {
    const j = await request(`/dispatches${qs(params)}`, { signal });
    return { rows: j.data || [], total: j.total || 0 };
};
export const getDispatch = (id, signal) => data(request(`/dispatches/${encodeURIComponent(id)}`, { signal }));
export const cancelDispatch = (id) => data(request(`/dispatches/${encodeURIComponent(id)}/cancel`, { method: 'POST', body: {} }));
export const rescheduleDispatch = (id, scheduleAt) => data(request(`/dispatches/${encodeURIComponent(id)}/reschedule`, { method: 'POST', body: { scheduleAt } }));
export const resendDispatch = (id, target = 'unread') => data(request(`/dispatches/${encodeURIComponent(id)}/resend`, { method: 'POST', body: { target } }));
export const getStats = (days = 7, signal) => data(request(`/stats${qs({ days })}`, { signal }));
export const getInsights = (days = 14, signal) => data(request(`/insights${qs({ days })}`, { signal }));

// templates
export const listTemplates = (signal) => data(request('/templates', { signal }));
export const createTemplate = (body) => data(request('/templates', { method: 'POST', body }));
export const updateTemplate = (id, body) => data(request(`/templates/${encodeURIComponent(id)}`, { method: 'PUT', body }));
export const deleteTemplate = (id) => data(request(`/templates/${encodeURIComponent(id)}`, { method: 'DELETE' }));

// public broadcasts
export const listBroadcasts = (params = {}, signal) => data(request(`/broadcasts${qs(params)}`, { signal }));
export const createBroadcast = (body) => data(request('/broadcasts', { method: 'POST', body }));
export const archiveBroadcast = (id) => data(request(`/broadcasts/${encodeURIComponent(id)}/archive`, { method: 'POST', body: {} }));
